import React, { useState, useCallback } from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Checkbox from '@mui/joy/Checkbox';
import IconButton from '@mui/joy/IconButton';
import Chip from '@mui/joy/Chip';
import Card from '@mui/joy/Card';
import {
  Envelope as EmailIcon,
  Phone as PhoneIcon,
  ChatCircle as SmsIcon,
  Calendar as EventIcon,
  ClipboardText as TaskIcon,
  CaretDown as ExpandMoreIcon,
  CaretUp as ExpandLessIcon,
  Check as DoneIcon,
  Alarm as SnoozeIcon,
  Envelope as EmailOutlinedIcon,
  Phone as PhoneOutlinedIcon,
  ChatCircle as SmsOutlinedIcon,
} from '@phosphor-icons/react';
import Link from '@mui/joy/Link';
import { format } from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';
import type { InboxItem } from '../../types/inbox';
import InlineEmailView from './InlineEmailView';
import InlineCallView from './InlineCallView';
import InlineSMSView from './InlineSMSView';
import InlineTaskView from './InlineTaskView';
import ContextChips, { generateContextChips } from './ContextChips';

interface UnifiedInboxItemProps {
  item: InboxItem;
  isSelected: boolean;
  isExpanded: boolean;
  onToggleSelect: (checked: boolean) => void;
  onToggleExpand: () => void;
  onDone: () => void;
  onSnooze: () => void;
  onEmail: () => void;
  onCall: () => void;
  onText: () => void;
  onReply?: (content: string, type: 'email' | 'sms') => void;
  onReplyStateChange?: (itemId: number, isReplying: boolean) => void;
}

/**
 * The contact's name, linking to their record. Falls back to plain text when
 * the item isn't matched to a contact (there is nowhere to go).
 *
 * stopPropagation keeps the row's click-to-expand from firing on the way out —
 * clicking the name should open the contact, not toggle the row.
 */
const ContactLink: React.FC<{
  item: InboxItem;
  level: 'body-sm' | 'body-md';
  fontWeight?: 'bold' | 'normal';
}> = ({ item, level, fontWeight }) => {
  const label = item.lead_name || 'Unknown';

  if (!item.lead) {
    return (
      <Typography level={level} fontWeight={fontWeight} noWrap sx={{ minWidth: 0, flex: '0 1 auto' }}>
        {label}
      </Typography>
    );
  }

  return (
    <Link
      component={RouterLink}
      to={`/crm/contacts/${item.lead}`}
      level={level}
      color="primary"
      onClick={(event) => event.stopPropagation()}
      sx={{ minWidth: 0, flex: '0 1 auto', fontWeight }}
    >
      {label}
    </Link>
  );
};

/**
 * The project the item belongs to, linking to the job.
 *
 * Inbound texts, calls and email are matched to the contact's open job on
 * arrival, so the row can say which roof it is about instead of sending the
 * user off to look it up. Task rows print the job in their title already.
 */
const JobLink: React.FC<{ item: InboxItem }> = ({ item }) => {
  if (!item.job || !item.job_name) return null;

  return (
    <Link
      component={RouterLink}
      to={`/operations/jobs/${item.job}`}
      level="body-xs"
      color="neutral"
      onClick={(event) => event.stopPropagation()}
      sx={{
        minWidth: 0,
        flex: '0 1 auto',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {item.job_name}
    </Link>
  );
};

const getChannelIcon = (channel: string, size: 'small' | 'medium' = 'small') => {
  const fontSize = size === 'small' ? 20 : 24;
  switch (channel) {
    case 'email':
      return <EmailIcon size={fontSize} />;
    case 'call':
      return <PhoneIcon size={fontSize} />;
    case 'sms':
      return <SmsIcon size={fontSize} />;
    case 'meeting':
      return <EventIcon size={fontSize} />;
    case 'task':
      return <TaskIcon size={fontSize} />;
    default:
      return <EmailIcon size={fontSize} />;
  }
};

const getChannelPrefix = (channel: string): string => {
  switch (channel) {
    case 'email':
      return '(EMAIL)';
    case 'call':
      return '(CALL)';
    case 'sms':
      return '(SMS)';
    case 'meeting':
      return '(MEETING)';
    case 'task':
      return '(TASK)';
    default:
      return '';
  }
};

const UnifiedInboxItem: React.FC<UnifiedInboxItemProps> = ({
  item,
  isSelected,
  isExpanded,
  onToggleSelect,
  onToggleExpand,
  onDone,
  onSnooze,
  onEmail,
  onCall,
  onText,
  onReply,
  onReplyStateChange,
}) => {
  const [isReplying, setIsReplying] = useState(false);
  const contextChips = generateContextChips(item);
  const formattedDate = format(new Date(item.last_event_at), 'MMM d');
  
  const handleContentClick = useCallback((e: React.MouseEvent) => {
    // Prevent expand when clicking on interactive elements
    if ((e.target as HTMLElement).closest('button, input, a')) {
      return;
    }
    onToggleExpand();
  }, [onToggleExpand]);

  const handleReplyStateChange = useCallback((isReplying: boolean) => {
    setIsReplying(isReplying);
    if (onReplyStateChange) {
      onReplyStateChange(item.id, isReplying);
    }
  }, [item.id, onReplyStateChange]);

  const renderInlineContent = () => {
    switch (item.channel) {
      case 'email':
        return <InlineEmailView item={item} onReply={onReply} onReplyStateChange={handleReplyStateChange} />;
      case 'call':
        return <InlineCallView item={item} onCall={onCall} />;
      case 'sms':
        return <InlineSMSView item={item} onReply={onReply} onReplyStateChange={handleReplyStateChange} />;
      case 'task':
        return <InlineTaskView item={item} onComplete={onDone} />;
      default:
        return (
          <Box sx={{ p: 3, bgcolor: 'background.level1', borderRadius: 'sm' }}>
            <Typography level="body-md">{item.preview}</Typography>
          </Box>
        );
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 0.5,
        transition: 'all 0.2s ease',
        bgcolor: isExpanded ? 'background.level1' : 'background.surface',
        '&:hover': {
          bgcolor: isExpanded ? 'background.level1' : 'background.level2',
        },
      }}
    >
      {/* Collapsed/Header State */}
      <Box
        onClick={handleContentClick}
        sx={{
          cursor: 'pointer',
          px: { xs: 1.5, md: 2 },
          py: 0.5,
        }}
      >
        <Stack direction="row" spacing={{ xs: 1, md: 2 }} alignItems="center">
          {/* Checkbox */}
          <Checkbox
            size="md"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onToggleSelect(e.target.checked);
            }}
          />

          {/* Channel Icon */}
          <Box
            sx={{
              color: item.unread ? 'primary.500' : 'text.secondary',
              display: { xs: 'none', sm: 'flex' },
            }}
          >
            {getChannelIcon(item.channel)}
          </Box>

          {/* Main Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {isExpanded ? (
              // Expanded Header
              <Stack spacing={1}>
                <Typography level="body-md" fontWeight="md">
                  {getChannelPrefix(item.channel)} {item.subject}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {item.channel === 'task' ? (
                    <Typography level="body-sm" color="primary">
                      {`${item.subject}${item.job_name ? ` - ${item.job_name}` : ''}`}
                    </Typography>
                  ) : (
                    <>
                      <ContactLink item={item} level="body-sm" />
                      <JobLink item={item} />
                    </>
                  )}
                  {item.lead_data?.is_vip && (
                    <Chip size="sm" color="warning" variant="soft">VIP</Chip>
                  )}
                </Stack>
              </Stack>
            ) : (
              // Collapsed Content
              <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ minWidth: 0 }}
                >
                  {item.channel === 'task' ? (
                    <Typography
                      level="body-sm"
                      fontWeight={item.unread ? 'bold' : 'normal'}
                      noWrap
                      sx={{ minWidth: 0, flex: '0 1 auto' }}
                    >
                      {`${item.subject}${item.job_name ? ` - ${item.job_name}` : ''}`}
                    </Typography>
                  ) : (
                    <ContactLink
                      item={item}
                      level="body-sm"
                      fontWeight={item.unread ? 'bold' : 'normal'}
                    />
                  )}
                  {item.channel === 'email' && item.from_email && (
                    <Typography
                      level="body-xs"
                      noWrap
                      sx={{
                        color: 'text.secondary',
                        display: { xs: 'none', md: 'inline' },
                        minWidth: 0,
                      }}
                    >
                      &lt;{item.from_email}&gt;
                    </Typography>
                  )}
                  {item.unread && (
                    <Chip size="sm" color="primary" variant="soft" sx={{ flexShrink: 0 }}>
                      New
                    </Chip>
                  )}
                  <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5, flexShrink: 0 }}>
                    {contextChips.length > 0 && contextChips.slice(0, 2).map((chip) => (
                      <ContextChips key={chip.id} chips={[chip]} />
                    ))}
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <Typography
                    level="body-xs"
                    noWrap
                    sx={{ color: 'text.secondary', minWidth: 0, flex: '0 1 auto' }}
                  >
                    {item.subject}
                  </Typography>
                  {item.channel !== 'task' && <JobLink item={item} />}
                </Stack>
              </Stack>
            )}
          </Box>

          {/* Right Side Actions/Info */}
          {isExpanded ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
                <Button
                  variant="soft"
                  color="success"
                  size="sm"
                  startDecorator={<DoneIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDone();
                  }}
                >
                  DONE
                </Button>
                <Button
                  variant="soft"
                  color="warning"
                  size="sm"
                  startDecorator={<SnoozeIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSnooze();
                  }}
                >
                  SNOOZE
                </Button>
              </Box>
              <Typography
                level="body-sm"
                sx={{ color: 'text.secondary', ml: { md: 1 }, display: { xs: 'none', sm: 'inline' } }}
              >
                {formattedDate}
              </Typography>
              <IconButton
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand();
                }}
              >
                <ExpandLessIcon />
              </IconButton>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                {formattedDate}
              </Typography>
              <IconButton
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand();
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Expanded Content */}
      <Box
        sx={{
          overflow: 'hidden',
          transition: 'max-height 0.3s ease',
          maxHeight: isExpanded ? (item.channel === 'task' ? 'none' : (isReplying ? '900px' : '700px')) : 0,
        }}
      >
        {isExpanded && (
          <Box sx={{ px: { xs: 1.5, md: 2 }, py: 0.5, pt: 0 }}>
            {/* Mobile-only DONE/SNOOZE row */}
            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 1.5, display: { xs: 'flex', md: 'none' } }}
            >
              <Button
                variant="soft"
                color="success"
                size="sm"
                startDecorator={<DoneIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  onDone();
                }}
                sx={{ flex: 1 }}
              >
                DONE
              </Button>
              <Button
                variant="soft"
                color="warning"
                size="sm"
                startDecorator={<SnoozeIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  onSnooze();
                }}
                sx={{ flex: 1 }}
              >
                SNOOZE
              </Button>
            </Stack>

            {/* Communication Action Buttons - hide for tasks */}
            {item.channel !== 'task' && (
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  startDecorator={<EmailOutlinedIcon />}
                  onClick={onEmail}
                  sx={{ flex: { xs: 1, md: 'none' } }}
                >
                  EMAIL
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  startDecorator={<PhoneOutlinedIcon />}
                  onClick={onCall}
                  sx={{ flex: { xs: 1, md: 'none' } }}
                >
                  CALL
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  startDecorator={<SmsOutlinedIcon />}
                  onClick={onText}
                  sx={{ flex: { xs: 1, md: 'none' } }}
                >
                  TEXT
                </Button>
              </Stack>
            )}

            {/* Task details grow with their related records; messages scroll within the preview. */}
            <Box
              sx={{
                bgcolor: 'background.surface',
                borderRadius: 'md',
                border: '1px solid',
                borderColor: 'divider',
                maxHeight: item.channel === 'task' ? 'none' : (isReplying ? '500px' : '400px'),
                overflow: item.channel === 'task' ? 'visible' : 'auto',
              }}
            >
              {renderInlineContent()}
            </Box>

            {/* Context Chips if any */}
            {contextChips.length > 0 && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 2 }}>
                {contextChips.map((chip) => (
                  <ContextChips key={chip.id} chips={[chip]} />
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default UnifiedInboxItem;